import { type TokenConfig, getOrgConfig, type BearerToken } from '@koala/sdk/v4';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { queryKeys } from './queries';
import { createHttpClient, createHttpClientWithToken } from '@/services/client';
import { getOrigin, __DANGEROUS_EMPTY_REQUEST_ORIGIN } from '@/utils';
import { isRetryableError } from '@/utils/networkErrors';
import { retryPromiseWithBackoff } from '@/utils/requests';

const ORG_MAX_RETRIES = 2;
const ORG_RETRY_BASE_MS = 300;
const ORG_RETRY_MAX_MS = 2000;

/**
 * This hook can be used to fetch org config without using any instance of React or its hooks.
 * For files where no instance of React is available
 */
export const getAndUseOrganization = async (host: string) => {
  try {
    let origin: string;
    if (typeof window !== 'undefined') {
      origin = getOrigin(window.location.host);
    } else {
      origin = getOrigin(host);
    }
    return await getOrgConfig({ client: createHttpClient({ origin }) });
  } catch (e) {
    console.error('Failed to get organization config', e);
  }
};

// fetch the organization, which is needed so we can pass the org id into the config request
// this may be cached depending on if its the first or nth time this request is being made
export function useOrganization() {
  /**
   * Make sure that `useFooterQuery` is prefetched before
   * this component renders, otherwise it will explode!
   * @see __DANGEROUS_EMPTY_REQUEST_ORIGIN
   */
  let origin: string;
  if (typeof window !== 'undefined') {
    origin = getOrigin(window.location.host);
  } else {
    origin = getOrigin(__DANGEROUS_EMPTY_REQUEST_ORIGIN);
  }

  return useQuery({
    queryKey: [queryKeys.organization],
    queryFn: () =>
      getOrgConfig({
        client: createHttpClient({ origin }),
      }),
  });
}

// fetch the organization, which is needed so we can pass the org id into the config request
// this may be cached depending on if its the first or nth time this request is being made
export async function getOrganization(
  queryClient: QueryClient,
  origin: string,
  token?: BearerToken | null,
): Promise<TokenConfig> {
  // The retry runs inside the queryFn, not through React Query's `retry` option. React Query
  // pauses a retry while the document is hidden and resumes it only via a mounted client's
  // focus listener — and `_app.getInitialProps` builds a fresh, never-mounted client per
  // navigation. With coo-api unreachable in a background tab, that left exactly one
  // `GET /v1/config`, a promise that never settled, and an in-app page change that never
  // completed (TEAM-14780 QA). Same budget and backoff as before.
  //
  // `networkMode: 'always'` for the same reason, one step earlier: React Query's default
  // (`'online'`) does not even start the FIRST attempt while `navigator.onLine` is false — it
  // pauses, and only a mounted client's online listener resumes it. With it, the fetch runs,
  // fails fast, and the caller's catch lets the navigation complete.
  return await queryClient.fetchQuery(
    [queryKeys.organization],
    () =>
      retryPromiseWithBackoff(
        () => getOrgConfig({ client: createHttpClientWithToken({ origin, token }) }),
        {
          attempts: ORG_MAX_RETRIES + 1,
          baseMs: ORG_RETRY_BASE_MS,
          maxMs: ORG_RETRY_MAX_MS,
          predicate: isRetryableError,
        },
      ),
    { staleTime: 60000, retry: false, networkMode: 'always' },
  );
}
